import { getPrecedingNodesUntilIterator, type PrecedingIteratorData } from './flow';
import { getIteratorRunCount } from './iterator.utils';
import type { NodeId } from 'web';
import type { IteratorRunMode, Edge, Node } from '@/types/node';

export type IteratorRunModeTarget = {
  iterators: PrecedingIteratorData[];
  nodeId: NodeId;
};

const getDirectRelevantSourceIds = (nodeId: NodeId, edges: Edge[], relevantNodeIds: Set<NodeId>): NodeId[] => {
  return edges.filter((edge) => edge.target === nodeId && relevantNodeIds.has(edge.source)).map((edge) => edge.source);
};

const getDirectIteratorBearingSourceIds = (
  directRelevantSourceIds: NodeId[],
  iteratorIds: Set<NodeId>,
  nodes: Node[],
  edges: Edge[],
  modelNodesInBatch: Set<NodeId>,
): NodeId[] => {
  return directRelevantSourceIds.filter((sourceId) => {
    if (iteratorIds.has(sourceId)) {
      return true;
    }

    return !!getPrecedingNodesUntilIterator(sourceId, nodes, edges, modelNodesInBatch)?.iterators.length;
  });
};

const inheritsIteratorCombinationFromSingleSource = (
  directIteratorBearingSourceIds: NodeId[],
  iteratorIds: Set<NodeId>,
): boolean => {
  if (directIteratorBearingSourceIds.length !== 1) {
    return false;
  }

  return !iteratorIds.has(directIteratorBearingSourceIds[0]);
};

export const getIteratorRunModeTargets = (
  nodeId: NodeId,
  nodes: Node[],
  edges: Edge[],
  modelNodesInBatch: Set<NodeId> = new Set(),
): IteratorRunModeTarget[] => {
  const result = getPrecedingNodesUntilIterator(nodeId, nodes, edges, modelNodesInBatch);
  if (!result) {
    return [];
  }

  const pathNodeIds = new Set(result.nodes.map((node) => node.id));

  return result.nodes
    .map((pathNode) => {
      const targetResult = getPrecedingNodesUntilIterator(pathNode.id, nodes, edges, modelNodesInBatch);
      if (!targetResult) {
        return null;
      }

      const directRelevantSourceIds = getDirectRelevantSourceIds(pathNode.id, edges, pathNodeIds);
      const iterators = targetResult.iterators;
      const iteratorIds = new Set(iterators.map((iterator) => iterator.iteratorNode.id));
      const directIteratorBearingSourceIds = getDirectIteratorBearingSourceIds(
        directRelevantSourceIds,
        iteratorIds,
        nodes,
        edges,
        modelNodesInBatch,
      );

      if (
        iteratorIds.size <= 1 ||
        inheritsIteratorCombinationFromSingleSource(directIteratorBearingSourceIds, iteratorIds)
      ) {
        return null;
      }

      return {
        iterators,
        nodeId: pathNode.id,
      };
    })
    .filter((target): target is IteratorRunModeTarget => target !== null);
};

export const mergeIteratorRunModeTargetsByNodeId = (targets: IteratorRunModeTarget[]): IteratorRunModeTarget[] => {
  const targetsByNodeId = new Map<NodeId, IteratorRunModeTarget>();

  targets.forEach((target) => {
    const existingTarget = targetsByNodeId.get(target.nodeId);
    if (!existingTarget) {
      targetsByNodeId.set(target.nodeId, target);
      return;
    }

    const existingIteratorKeys = new Set(
      existingTarget.iterators.map((iterator) => `${iterator.iteratorNode.id}:${iterator.inputKey}`),
    );
    targetsByNodeId.set(target.nodeId, {
      ...existingTarget,
      iterators: [
        ...existingTarget.iterators,
        ...target.iterators.filter(
          (iterator) => !existingIteratorKeys.has(`${iterator.iteratorNode.id}:${iterator.inputKey}`),
        ),
      ],
    });
  });

  return Array.from(targetsByNodeId.values());
};

export const getIteratorRunModeTargetNodeIds = (
  nodeId: NodeId,
  nodes: Node[],
  edges: Edge[],
  modelNodesInBatch: Set<NodeId> = new Set(),
): NodeId[] => {
  return getIteratorRunModeTargets(nodeId, nodes, edges, modelNodesInBatch).map((target) => target.nodeId);
};

const canReachTarget = (sourceNodeId: NodeId, targetNodeIds: Set<NodeId>, edges: Edge[]): boolean => {
  const stack = edges.filter((edge) => edge.source === sourceNodeId).map((edge) => edge.target);
  const visited = new Set<NodeId>();

  while (stack.length > 0) {
    const nodeId = stack.pop()!;
    if (visited.has(nodeId)) {
      continue;
    }
    visited.add(nodeId);

    if (targetNodeIds.has(nodeId)) {
      return true;
    }

    edges.forEach((edge) => {
      if (edge.source === nodeId) {
        stack.push(edge.target);
      }
    });
  }

  return false;
};

const filterLeafRunModeTargets = (targets: IteratorRunModeTarget[], edges: Edge[]): IteratorRunModeTarget[] => {
  const targetNodeIds = new Set(targets.map((target) => target.nodeId));
  return targets.filter((target) => {
    const otherTargetNodeIds = new Set(targetNodeIds);
    otherTargetNodeIds.delete(target.nodeId);
    return !canReachTarget(target.nodeId, otherTargetNodeIds, edges);
  });
};

export const getIteratorRunModeTargetsTotalRuns = (
  targets: IteratorRunModeTarget[],
  runMode: IteratorRunMode,
  getIteratorValueCount: (iterator: PrecedingIteratorData) => number,
  edges: Edge[] = [],
): number => {
  return filterLeafRunModeTargets(targets, edges).reduce((totalRuns, target) => {
    const valueCountsByIteratorId = new Map<NodeId, number>();
    target.iterators.forEach((iterator) => {
      if (!valueCountsByIteratorId.has(iterator.iteratorNode.id)) {
        valueCountsByIteratorId.set(iterator.iteratorNode.id, getIteratorValueCount(iterator));
      }
    });

    return totalRuns + getIteratorRunCount(Array.from(valueCountsByIteratorId.values()), runMode);
  }, 0);
};
